<template>
  <div class="filter-range">
    <span class="filter-range__label">{{ label }}</span>
    <div class="filter-range__inputs">
      <el-input
        v-model="minModel"
        size="small"
        inputmode="decimal"
        clearable
        :disabled="disabled"
        class="filter-range__input"
        :class="{ 'is-error': Boolean(minError) || Boolean(minDecimalError) }"
      />
      <span class="filter-range__suffix">以上</span>
      <el-input
        v-model="maxModel"
        size="small"
        inputmode="decimal"
        clearable
        :disabled="disabled"
        class="filter-range__input"
        :class="{ 'is-error': Boolean(maxError) || Boolean(maxDecimalError) }"
      />
      <span class="filter-range__suffix">未満</span>
    </div>
    <span class="filter-range__error">{{ errorMessage }}</span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'

import {
  exceedsDecimalScale,
  isPartialInput,
  parseNumeric
} from '@/client/features/twitter_benchmark_analytics/utils/numericRangeInput'

const DECIMAL_PLACE_LABELS: Record<number, string> = {
  1: '一',
  2: '二',
  3: '三'
}

/** decimalScale を超える桁数の小数が入力された際のエラー文言を組み立てる。 */
const buildDecimalScaleErrorMessage = (label: string, decimalScale: number): string => {
  if (decimalScale <= 0) return `${label}の値は整数で入力してください。`
  const placeLabel = DECIMAL_PLACE_LABELS[decimalScale] ?? `${decimalScale}`
  return `${label}の値は小数点第${placeLabel}位までです。`
}

/**
 * 「{ラベル} [Min] 以上 〜 [Max] 未満」形式の数値レンジ入力（エラー表示で高さが変わらないよう 1 行分の領域を常に確保）。
 */
export default defineComponent({
  name: 'FilterRange',

  props: {
    label: {
      type: String,
      required: true
    },
    minValue: {
      type: Number,
      default: undefined
    },
    maxValue: {
      type: Number,
      default: undefined
    },
    minError: {
      type: String,
      default: ''
    },
    maxError: {
      type: String,
      default: ''
    },
    decimalScale: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:minValue', 'update:maxValue'],

  setup(props, { emit }) {
    const minDecimalError = ref('')
    const maxDecimalError = ref('')

    const errorMessage = computed(
      () => minDecimalError.value || maxDecimalError.value || props.minError || props.maxError
    )

    const minText = ref(typeof props.minValue === 'number' ? String(props.minValue) : '')
    const maxText = ref(typeof props.maxValue === 'number' ? String(props.maxValue) : '')

    watch(
      () => props.minValue,
      value => {
        if (parseNumeric(minText.value, props.decimalScale) !== value) {
          minText.value = typeof value === 'number' ? String(value) : ''
          minDecimalError.value = ''
        }
      }
    )
    watch(
      () => props.maxValue,
      value => {
        if (parseNumeric(maxText.value, props.decimalScale) !== value) {
          maxText.value = typeof value === 'number' ? String(value) : ''
          maxDecimalError.value = ''
        }
      }
    )

    const minModel = computed<string>({
      get: () => minText.value,
      set: value => {
        minText.value = value
        const trimmed = value.trim()
        if (isPartialInput(trimmed, props.decimalScale)) return
        if (exceedsDecimalScale(trimmed, props.decimalScale)) {
          minDecimalError.value = buildDecimalScaleErrorMessage(props.label, props.decimalScale)
          emit('update:minValue', undefined)
          return
        }
        minDecimalError.value = ''
        emit('update:minValue', parseNumeric(value, props.decimalScale))
      }
    })
    const maxModel = computed<string>({
      get: () => maxText.value,
      set: value => {
        maxText.value = value
        const trimmed = value.trim()
        if (isPartialInput(trimmed, props.decimalScale)) return
        if (exceedsDecimalScale(trimmed, props.decimalScale)) {
          maxDecimalError.value = buildDecimalScaleErrorMessage(props.label, props.decimalScale)
          emit('update:maxValue', undefined)
          return
        }
        maxDecimalError.value = ''
        emit('update:maxValue', parseNumeric(value, props.decimalScale))
      }
    })

    return {
      minModel,
      maxModel,
      minDecimalError,
      maxDecimalError,
      errorMessage
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.filter-range {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__label {
    font-size: 12px;
    color: $text-color;
  }

  &__inputs {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  &__input {
    width: 96px;

    &.is-error {
      :deep(.el-input__inner) {
        border-color: $danger-color !important;
      }
    }
  }

  &__suffix {
    font-size: 12px;
    color: $sub-text-color;
  }

  &__error {
    height: 16px;
    overflow: hidden;
    font-size: 12px;
    line-height: 16px;
    color: $danger-color;
    white-space: nowrap;
  }
}
</style>
